/**
 * Table of contents component that detects the H2 headings on the page
 * and creates a list of links to them, detecting which one is currently
 * in view and highlighting it.
 *
 * Currently used/designed for the blog, guides, and lessons pages.
 */

import React, {useState, useEffect} from 'react';
import cx from 'classnames';
import {throttle, findLastIndex} from 'lodash';
import {ListBulletIcon} from '@heroicons/react-v2/24/outline';

import {ChevronIcon} from './svg';

export const generateTOC = (selector = 'h2') => {
  const elements = document.querySelectorAll(selector);
  const sections = [];
  elements.forEach((el) => {
    sections.push({id: el.id, text: el.textContent});
  });
  return sections;
};

const TableOfContents = ({
  sections,
  mobile,
  defaultOpen = false,
  scrollContainer = window,
  header = 'Article contents',
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const [active, setActive] = useState(undefined);

  const onClick = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    const containerTop = scrollContainer === window
      ? scrollContainer.scrollY // window doesn't have scrollTop
      : scrollContainer.scrollTop;
    const top = el.getBoundingClientRect().top + containerTop - 80;
    if (el) scrollContainer.scrollTo({top, behavior: 'smooth'});
  };

  useEffect(() => {
    const handleScroll = throttle(() => {
      const activeIndex = findLastIndex(sections, (section) => {
        const el = document.getElementById(section.id);
        return el?.getBoundingClientRect().top < 200;
      });
      const isNearBottom = Math.abs(scrollContainer.scrollHeight
        - scrollContainer.scrollTop
        - scrollContainer.offsetHeight) < 5; // 5px tolerance
      setActive(isNearBottom
        ? sections[sections.length - 1]?.id
        : sections[activeIndex]?.id);
    }, 100);

    scrollContainer.addEventListener('scroll', handleScroll);
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, [sections, scrollContainer]);

  return (
    <div className={cx('relative w-full mt-4 bg-white rounded-lg p-4', {
      'shadow border border-gray-100 overflow-hidden': mobile,
    })}
    >
      <div className="lg:pl-2">
        {mobile ? (
          <button className="w-full flex justify-between" type="button" onClick={() => setOpen(!open)}>
            <span className="flex items-center text-base text-gray-800 font-medium tracking-tight">
              <ListBulletIcon className="w-6 h-6 mr-2 text-gray-600" />
              {header}
            </span>
            <ChevronIcon className="text-gray-500" />
          </button>
        ) : (
          <span className="flex items-center text-base text-gray-800 font-medium tracking-tight">
            {header}
          </span>
        )}
      </div>
      {(open || !mobile) && (
        <div className={cx({'h-3/4 overflow-visible scrollbar-hidden': !mobile})}>
          <ul className="mt-2 space-y-2 leading-snug text-sm font-regular">
            {sections?.map((section) => (
              <li
                className={cx('text-gray-500 hover:text-gray-700 border-l-2 lg:pl-1.5 border-transparent', {
                  truncate: mobile,
                  'text-indigo-600 hover:text-indigo-600 font-medium border-indigo-600': active === section.id,
                })}
                key={section.id}
              >
                <a
                  className={cx({block: !mobile})}
                  href={`#${section.id}`}
                  onClick={(e) => onClick(e, section.id)}
                >
                  {section.text}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default TableOfContents;
